/**
 * Mounting the shared tenancy hints for this app.
 *
 * `ui-core`'s pills and preferences panel read three things from context: the
 * caller's saved defaults, this app's policy table, and where to send someone who
 * wants to change them. All three are the dashboard's to supply — the store is
 * the saved-views blob, the table is `@/config`, and the destination is this
 * app's settings route — which is exactly why the components take them rather
 * than importing them.
 *
 * Mounted under `DashboardPrefsProvider` (the store it adapts) and inside the
 * router, since `useNavigate` needs one. A surface rendered outside this still
 * draws correct pills; it just cannot offer the link.
 */
import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { ScopeHintProvider, SCOPE_PREF_ANCHOR, type ScopeHintTarget } from "@authe/ui-core";
import { ROUTES } from "@/config";
import { useDashboardPolicies } from "./enforcedPolicy";
import { useMyTeams } from "./useMyTeams";
import { useScopePrefs } from "./scopePrefs";

export function DashboardScopeHints({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const navigate = useNavigate();
  const prefs = useScopePrefs();
  // The table narrowed by the enforced policy: the panel, the pills and the
  // attach control all read it from this provider.
  const policies = useDashboardPolicies();
  const teams = useMyTeams();
  // Only to WARN: a "Specific teams" write default with nobody to share with
  // degrades to private on every attach, and the preferences panel was the one
  // surface offering that choice without saying so.
  const teamIds = React.useMemo(() => teams.map((team) => team.id), [teams]);
  // The CARD, not just the route. A pill that names a tier and then drops the
  // reader at the top of a settings page holding a dozen selects has answered
  // "which tier?" and not "where do I change it?", which is the actual ask.
  const onConfigure = React.useCallback(
    (target: ScopeHintTarget) =>
      void navigate({ to: ROUTES.settingsDefaults, hash: SCOPE_PREF_ANCHOR[target] }),
    [navigate],
  );
  return (
    <ScopeHintProvider
      prefs={prefs}
      policies={policies}
      teamIds={teamIds}
      onConfigure={onConfigure}
    >
      {children}
    </ScopeHintProvider>
  );
}
